import React from 'react'
import { Receipt, LinkedReceipt } from '../../services/receipts/models'
import { PdfPreview } from '../pdf-preview'
import { useTranslation } from 'react-i18next'
import { ListItemRow } from '../layout/item-collection'
import type { ViewMode } from '../../hooks/use-view-mode'

interface ReceiptListItemProps {
  receipt: Receipt | LinkedReceipt
  viewMode?: ViewMode
  /** List view only: extra metadata rendered under the name. */
  meta?: React.ReactNode
  /** List view only: actions rendered inline on the right of the row. */
  actions?: React.ReactNode
}

const getFileExtension = (url: string): string => {
  try {
    const urlWithoutQuery = url.split('?')[0]
    const filename = urlWithoutQuery.split('/').pop() || ''
    const extension = filename.split('.').pop() || ''
    return extension.toLowerCase()
  } catch (error) {
    console.error('Error extracting file extension:', error)
    return ''
  }
}

export const ReceiptListItem: React.FC<ReceiptListItemProps> = ({
  receipt,
  viewMode = 'card',
  meta,
  actions,
}) => {
  const { t } = useTranslation()
  const fileExtension = receipt.asset ? getFileExtension(receipt.asset) : ''
  const isPdf = fileExtension === 'pdf'

  if (viewMode === 'list') {
    return (
      <ListItemRow data-testid={`receipt-list-item-${receipt.id}`}>
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded bg-gray-100">
            {receipt.asset ? (
              isPdf ? (
                <PdfPreview file={receipt.asset} cacheKey={receipt.id} width={48} />
              ) : (
                <img
                  src={receipt.asset}
                  alt={receipt.name}
                  className="h-12 w-12 rounded object-cover"
                />
              )
            ) : (
              <span className="text-center text-[10px] leading-tight text-text-tertiary">
                {t('receipts.noImage')}
              </span>
            )}
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="truncate font-semibold text-text-primary" title={receipt.name}>
              {receipt.name}
            </h3>
            {meta ? (
              <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-1 text-sm text-text-placeholder">
                {meta}
              </div>
            ) : null}
          </div>
        </div>
        {actions ? (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-1 sm:gap-2">
            {actions}
          </div>
        ) : null}
      </ListItemRow>
    )
  }

  return (
    <div
      className="flex flex-col items-center rounded-xl border border-border bg-surface p-6 shadow-sm transition-shadow hover:shadow-md"
      data-testid={`receipt-list-item-${receipt.id}`}
    >
      <div className="mb-3 flex h-24 w-24 items-center justify-center overflow-hidden rounded-md bg-gray-100">
        {receipt.asset ? (
          isPdf ? (
            <PdfPreview file={receipt.asset} cacheKey={receipt.id} width={96} />
          ) : (
            <img
              src={receipt.asset}
              alt={receipt.name}
              className="h-full w-full rounded object-cover"
            />
          )
        ) : (
          <div className="flex h-full w-full items-center justify-center text-xs text-text-tertiary">
            {t('receipts.noImage')}
          </div>
        )}
      </div>

      <p
        className="mb-2 line-clamp-2 flex min-h-[2.5rem] w-full items-center justify-center break-words text-center text-sm font-medium text-text-primary"
        title={receipt.name}
      >
        {receipt.name}
      </p>
    </div>
  )
}
